// Copyright (c) StrangeBee 2023

import { DescribeFieldOutput, FilterInput } from 'thehive-sdk';

const createFilter = (operator: string, entry: any, describeData?: DescribeFieldOutput): FilterInput => {
    const describeAttribute = describeData?.attributes.find(({ name }) => name === entry._field);

    const baseFilter: FilterInput = {
        field: entry._field,
        values: describeAttribute?.type === 'date' ? [entry._from, entry._to] : [entry._value],
        operator,
        describeAttribute,
        type: describeAttribute?.type || 'string',
    };

    return baseFilter;
};

const convertOperator = (operator: string): string => {
    switch (operator) {
        case '_and':
            return 'all';
        case '_or':
            return 'any';
        case '_not':
            return 'not';
        default:
            return operator;
    }
};

const parseEntry = (entry: any, operator: string, describeData?: DescribeFieldOutput): FilterInput | undefined => {
    if ('_field' in entry) {
        const filter = {
            ...createFilter(convertOperator(operator), entry, describeData),
            params: {
                strict: true,
            },
        };
        return filter;
    }

    if ('_match' in entry) {
        return createFilter(convertOperator(operator), entry._match, describeData);
    }

    if ('_like' in entry) {
        return createFilter(convertOperator(operator), entry._like, describeData);
    }

    if ('_contains' in entry) {
        return createFilter(operator === '_not' ? 'is empty' : 'is not empty', entry, describeData);
    }

    if ('_between' in entry) {
        return createFilter('custom', entry._between, describeData);
    }
};

export const convertAPIFilters = (filter?: any, describeData?: DescribeFieldOutput): FilterInput[] => {
    const filters = filter?._and as any[];

    return filters?.reduce((acc, entry) => {
        const entryTest = parseEntry(entry, '_and', describeData);

        if (entryTest) {
            return [...acc, entryTest];
        }

        if ('_or' in entry) {
            return [
                ...acc,
                entry._or.reduce((acc: FilterInput, filter: any) => {
                    const entry = parseEntry(filter, '_or', describeData);
                    return { ...entry, values: [...(acc.values || []), ...(entry?.values || [])] };
                }, {} as FilterInput),
            ];
        }

        if ('_and' in entry) {
            const ret = [
                ...acc,
                entry._and.reduce((acc: FilterInput, filter: any) => {
                    const entry = parseEntry(filter, '_and', describeData);

                    return { ...entry, values: [...(acc.values || []), ...(entry?.values || [])] };
                }, {} as FilterInput),
            ];

            return ret;
        }

        if ('_not' in entry) {
            let doubleEntry = entry._not;

            if ('_or' in entry._not) {
                doubleEntry = entry._not._or;
            }

            const entryTest = parseEntry(doubleEntry, '_not', describeData);

            if (entryTest) {
                return [...acc, entryTest];
            }

            return [
                ...acc,
                doubleEntry.reduce((acc: FilterInput, filter: any) => {
                    const entry = parseEntry(filter, '_not', describeData);
                    return { ...entry, values: [...(acc.values || []), ...(entry?.values || [])] };
                }, {} as FilterInput),
            ];
        }
    }, [] as FilterInput[]);
};
